import { Component, ElementRef, Input, OnDestroy, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

import { ValueAccessorBase } from '../../form/value-accessor';
import { HDateHour } from '@shared/src/datatypes/HDateHour';
import { DateRangeToken } from '@shared/src/enums/DateRangeToken';
import { DateRangeValueDto } from '@shared/src/dtos/base/filter/DateRangeValueDto';

export class AroundDurationOption {
  constructor(public totalMs: number, public labelKey: string) { }
}

// Posicions del panel principal respecte al trigger: preferentment a sota, amb
// fallback a sobre si no hi ha espai. El CDK ja s'encarrega de "push" dins del
// viewport si tampoc hi càpiga cap de les dues.
const PANEL_POSITIONS: ConnectedPosition[] = [
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
  { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 }
];

// Posicions del subpanel (Periode determinat / Al voltant d'un moment) respecte
// al panel principal: a la dreta, amb fallback a l'esquerra.
const SUBPANEL_POSITIONS: ConnectedPosition[] = [
  { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 4 },
  { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -4 }
];

@Component({
  selector: 'daterange-picker',
  templateUrl: './daterange-picker.component.html',
  styleUrls: ['./daterange-picker.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: DateRangePickerComponent, multi: true }
  ]
})
export class DateRangePickerComponent extends ValueAccessorBase<DateRangeValueDto, DateRangeValueDto> implements OnDestroy {

  public externalValue(value: DateRangeValueDto): DateRangeValueDto {
    return value;
  }

  public internalValue(value: DateRangeValueDto): DateRangeValueDto {
    return value || DateRangeValueDto.default();
  }

  // El panel (i el subpanel) es renderitzen via Angular CDK Overlay, que els penja
  // directament del document.body (cdk-overlay-container). Això és imprescindible
  // perque un simple 'position: fixed' deixa de funcionar en quant algun avantpassat
  // te un transform/filter/contain (molt habitual amb components Ionic), fent que el
  // panel flotant surti mal posicionat o quedi retallat pel contenidor.
  @ViewChild('panelTemplate') panelTemplateRef: TemplateRef<any>;
  @ViewChild('subpanelTemplate') subpanelTemplateRef: TemplateRef<any>;

  private panelOverlayRef: OverlayRef;
  private subPanelOverlayRef: OverlayRef;

  private isOutsideClick(target: Node): boolean {
    if (this.elementRef.nativeElement.contains(target))
      return false;
    // Els popups propis (panel/subpanel) i els del datehour-picker (calendari/hora)
    // es renderitzen dins un cdk-overlay-container penjat directament del body.
    if (target instanceof Element && target.closest && target.closest('.cdk-overlay-container'))
      return false;
    return true;
  }

  private clickListener = (event: MouseEvent) => {
    if (!this.isOpen)
      return;
    if (this.isOutsideClick(event.target as Node))
      this.closeAll();
  };

  constructor(private elementRef: ElementRef, private overlay: Overlay, private viewContainerRef: ViewContainerRef) {
    super();
    document.addEventListener('click', this.clickListener, true);
  }

  ngOnDestroy() {
    document.removeEventListener('click', this.clickListener, true);
    this.disposeOverlays();
  }

  private disposeOverlays() {
    if (this.subPanelOverlayRef) {
      this.subPanelOverlayRef.dispose();
      this.subPanelOverlayRef = null;
    }
    if (this.panelOverlayRef) {
      this.panelOverlayRef.dispose();
      this.panelOverlayRef = null;
    }
  }

  // Llista per defecte, pensada per pantalles tecniques (logs, monitoratge).
  // Cada pantalla pot passar el seu propi subconjunt/ordre de tokens via [tokens].
  @Input() public tokens: DateRangeToken[] = [
    DateRangeToken.LAST1H,
    DateRangeToken.LAST6H,
    DateRangeToken.LAST12H,
    DateRangeToken.TODAY,
    DateRangeToken.YESTERDAY,
    DateRangeToken.LAST2D,
    DateRangeToken.LAST3D,
    DateRangeToken.LAST7D,
    DateRangeToken.ALLTIME
  ];

  // Quan la pantalla nomes vol periodes de calendari (dia/mes/any), els panells
  // "Start and end times" / "Around a time" (pensats per rangs de precisio horaria) sobren.
  @Input() public showAdvancedPanels = true;

  public readonly aroundDurations: AroundDurationOption[] = [
    new AroundDurationOption(60000, 'DATERANGE.AROUND.MIN1'),
    new AroundDurationOption(300000, 'DATERANGE.AROUND.MIN5'),
    new AroundDurationOption(900000, 'DATERANGE.AROUND.MIN15'),
    new AroundDurationOption(1800000, 'DATERANGE.AROUND.MIN30'),
    new AroundDurationOption(3600000, 'DATERANGE.AROUND.HOUR1'),
    new AroundDurationOption(7200000, 'DATERANGE.AROUND.HOUR2'),
    new AroundDurationOption(10800000, 'DATERANGE.AROUND.HOUR3'),
    new AroundDurationOption(86400000, 'DATERANGE.AROUND.DAY1'),
    new AroundDurationOption(172800000, 'DATERANGE.AROUND.DAY2'),
  ];

  public isOpen = false;
  public showCustomPanel = false;
  public showAroundPanel = false;

  public customFrom: HDateHour;
  public customTo: HDateHour;

  public anchorValue: HDateHour;
  public aroundDurationMs: number = 3600000;

  public toggleOpen() {
    if (this.isOpen) {
      this.closeAll();
    } else {
      this.openPanel();
    }
  }

  private openPanel() {
    let positionStrategy = this.overlay.position().flexibleConnectedTo(this.elementRef)
      .withPositions(PANEL_POSITIONS)
      .withFlexibleDimensions(false)
      .withPush(true)
      .withViewportMargin(8);

    this.panelOverlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: 'daterange-cdk-panel'
    });

    this.panelOverlayRef.attach(new TemplatePortal(this.panelTemplateRef, this.viewContainerRef));
    this.isOpen = true;
  }

  private openSubPanel() {
    if (this.subPanelOverlayRef) {
      this.subPanelOverlayRef.dispose();
      this.subPanelOverlayRef = null;
    }
    if (!this.panelOverlayRef)
      return;

    let positionStrategy = this.overlay.position().flexibleConnectedTo(this.panelOverlayRef.overlayElement)
      .withPositions(SUBPANEL_POSITIONS)
      .withFlexibleDimensions(false)
      .withPush(true)
      .withViewportMargin(8);

    this.subPanelOverlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: 'daterange-cdk-panel'
    });

    this.subPanelOverlayRef.attach(new TemplatePortal(this.subpanelTemplateRef, this.viewContainerRef));
  }

  public closeAll() {
    this.isOpen = false;
    this.showCustomPanel = false;
    this.showAroundPanel = false;
    this.disposeOverlays();
  }

  public selectToken(token: DateRangeToken) {
    this.value = DateRangeValueDto.relative(token);
    this.closeAll();
  }

  public openCustomPanel() {
    let current = this.value;
    this.customFrom = !current.isRelative() && current.dateFrom ? current.dateFrom : HDateHour.now().addHours(-1);
    this.customTo = !current.isRelative() && current.dateTo ? current.dateTo : HDateHour.now();
    this.showCustomPanel = true;
    this.showAroundPanel = false;
    this.openSubPanel();
  }

  public applyCustomPanel() {
    if (HDateHour.isNullOrNullValue(this.customFrom) || HDateHour.isNullOrNullValue(this.customTo))
      return;
    if (HDateHour.isGreaterThan(this.customFrom, this.customTo))
      return;
    this.value = DateRangeValueDto.absolute(this.customFrom, this.customTo);
    this.closeAll();
  }

  public openAroundPanel() {
    this.anchorValue = HDateHour.now();
    this.aroundDurationMs = 3600000;
    this.showAroundPanel = true;
    this.showCustomPanel = false;
    this.openSubPanel();
  }

  // El valor seleccionat és l'amplada TOTAL del rang, centrada en el moment: la data
  // d'inici i la de final queden a ± la meitat de la durada respecte al moment triat.
  public applyAroundPanel() {
    if (HDateHour.isNullOrNullValue(this.anchorValue) || !this.aroundDurationMs)
      return;

    let halfMs = this.aroundDurationMs / 2;
    let from = HDateHour.addMilliseconds(this.anchorValue, -halfMs);
    let to = HDateHour.addMilliseconds(this.anchorValue, halfMs);

    this.value = DateRangeValueDto.absolute(from, to);
    this.closeAll();
  }

  public isTokenSelected(token: DateRangeToken): boolean {
    return this.value && this.value.token === token;
  }
}
